import React, { useState, useEffect, useRef } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useAppUpdates } from "@/lib/appUpdatesStore";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, LogOut, User, Receipt, Menu, X, ShoppingCart, 
  ChevronDown, ChevronRight, MessageSquare, BookOpen, Bell, 
  FileText, Users, Truck, Megaphone, ClipboardList, CreditCard
} from "lucide-react";
import { toast } from "sonner";
import { pollGet, runPollTasks, startPolling } from "@/utils/layoutPolling";
import MobileStickyHeader from "@/components/layout/MobileStickyHeader";
import PullToRefresh from "@/components/layout/PullToRefresh";
import { playNotificationSound } from "@/utils/notificationSound";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collapsible Menu Group Component
const MenuGroup = ({ icon: Icon, label, children, badge, isExpanded, onToggle, isActive, title }) => {
  return (
    <div className="mb-1">
      <button
        onClick={onToggle}
        title={title || label}
        className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-700 font-medium"
            : "text-gray-700 hover:bg-gray-100"
        }`}
      >
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <Icon className="w-5 h-5 flex-shrink-0" />
          <span className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {badge > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center">
              {badge}
            </span>
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </div>
      </button>
      {isExpanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-gray-200 pl-2">
          {children}
        </div>
      )}
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ to, icon: Icon, label, badge, onClick, isActive, title }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      title={title || label}
      className={`flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-100 text-blue-700 font-medium"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
        <span className="text-sm whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[18px] text-center flex-shrink-0 ml-2">
          {badge}
        </span>
      )}
    </Link>
  );
};

// Standalone Menu Item Component
const StandaloneMenuItem = ({ to, icon: Icon, label, badge, onClick, isActive, title }) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      title={title || label}
      className={`flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
        isActive
          ? "bg-blue-50 text-blue-700 font-medium"
          : "text-gray-700 hover:bg-gray-100"
      }`}
    >
      <div className="flex items-center space-x-3 min-w-0 flex-1">
        <Icon className="w-5 h-5 flex-shrink-0" />
        <span className="font-medium whitespace-nowrap overflow-hidden text-ellipsis">{label}</span>
      </div>
      {badge > 0 && (
        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center flex-shrink-0 ml-2">
          {badge}
        </span>
      )}
    </Link>
  );
};

export default function FinanceLayout() {
  const { user, logout } = useAuth();
  const { count: appUpdatesCount } = useAppUpdates();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Badge counts
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const prevUnreadNotifRef = useRef(null);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [unreadChats, setUnreadChats] = useState({ admin: 0, gudang: 0, marketing: 0 });

  // Collapsible state
  const [expandedGroups, setExpandedGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('finance_sidebar_expanded');
      return saved ? JSON.parse(saved) : { orders: false, invoice: true, supplier: false, pusatInfo: false, chat: false };
    } catch {
      return { orders: false, invoice: true, supplier: false, pusatInfo: false, chat: false };
    }
  });

  // Save expanded state
  useEffect(() => {
    localStorage.setItem('finance_sidebar_expanded', JSON.stringify(expandedGroups));
  }, [expandedGroups]);

  // Auto-expand based on route
  useEffect(() => {
    const path = location.pathname;
    
    if (path.startsWith('/finance/orders')) {
      setExpandedGroups(prev => ({ ...prev, orders: true }));
    }
    if (path.includes('/finance/invoices') || path.includes('/finance/vouchers')) {
      setExpandedGroups(prev => ({ ...prev, invoice: true }));
    }
    if (path.includes('/finance/purchase-orders') || path.includes('/finance/suppliers')) {
      setExpandedGroups(prev => ({ ...prev, supplier: true }));
    }
    if (path.includes('/finance/knowledge-center') || path.includes('/finance/tutorial') || path.includes('/finance/announcements') || path.includes('/finance/apa-yang-baru')) {
      setExpandedGroups(prev => ({ ...prev, pusatInfo: true }));
    }
    if (path.includes('/finance/chats')) {
      setExpandedGroups(prev => ({ ...prev, chat: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const closeSidebar = () => setSidebarOpen(false);

  // RBAC: finance sub-roles. INVOICE → invoice menus only.
  // SUPPLIER → bayar supplier menus only. KEDUANYA / null → both.
  const financeRole = (user?.finance_role || "KEDUANYA").toUpperCase();
  const canSeeInvoiceMenu = financeRole === "INVOICE" || financeRole === "KEDUANYA";
  const canSeeSupplierMenu = financeRole === "SUPPLIER" || financeRole === "KEDUANYA";

  // Feb 2026 — Unified chat badge: source from /notifications/badge-summary
  // yang consistent across all roles. Sub-menu badge tetap pakai state lama
  // (unreadChats.admin/gudang/marketing) untuk detail per-room.
  const [chatBadgeTotal, setChatBadgeTotal] = useState(0);

  // Fetch badge counts
  // Okt 2026 (Fase 3) — request bersamaan dengan timeout, hasil gagal total
  // dilaporkan ke startPolling untuk backoff.
  const fetchCounts = async () => {
    const token = localStorage.getItem('token');
    if (!token) return true;
    const headers = { Authorization: `Bearer ${token}` };

    return runPollTasks([
      // Unified badge-summary — dipakai untuk parent Chat badge
      async () => {
        const bs = await pollGet(`${API}/notifications/badge-summary`, { headers });
        setChatBadgeTotal(bs.data?.chat?.total || 0);
      },
      // Unread notifications
      async () => {
        const notifRes = await pollGet(`${API}/notifications/unread-count`, { headers });
        const newUnread = notifRes.data.unread_count || 0;
        setUnreadNotifications(newUnread);
        if (prevUnreadNotifRef.current !== null && newUnread > prevUnreadNotifRef.current) {
          playNotificationSound();
        }
        prevUnreadNotifRef.current = newUnread;
      },
      // Unread announcements
      async () => {
        const announcementsRes = await pollGet(`${API}/knowledge-center/announcements/unread-count`, { headers });
        setUnreadAnnouncements(announcementsRes.data.unread_count || 0);
      },
      // Unread chats - coordination channels + direct chat marketing
      async () => {
        // Feb 2026 — Marketing chat uses direct_chat collection (separate
        // from coordination_channels). Kedua request jalan bersamaan; kalau
        // coordination-channels gagal, hasil tidak dipakai (sama seperti dulu).
        const [chatsRes, marketingUnread] = await Promise.all([
          pollGet(`${API}/coordination-channels`, { headers }),
          pollGet(`${API}/direct-chat/threads`, { headers })
            .then((threadsRes) => {
              const threads = threadsRes.data?.threads || [];
              return threads.filter(t => (t.unread_count || 0) > 0).length;
            })
            .catch(() => 0),
        ]);
        const channels = chatsRes.data?.channels || [];
        let adminUnread = 0, gudangUnread = 0;

        channels.forEach(ch => {
          const unread = ch.unread_count || 0;
          if (ch.channel_id?.includes('ADMIN')) adminUnread += unread;
          else if (ch.channel_id?.includes('GUDANG')) gudangUnread += unread;
        });

        setUnreadChats({ admin: adminUnread, gudang: gudangUnread, marketing: marketingUnread });
      },
    ]);
  };

  useEffect(() => {
    // Okt 2026 (Fase 3) — polling 30 detik lewat startPolling: tidak menumpuk,
    // berhenti saat tab tersembunyi atau offline, backoff saat gagal.
    const stopPolling = startPolling(fetchCounts);
    return () => {
      stopPolling();
    };
  }, []);

  // Check active routes
  const isInvoiceActive = ['/finance/invoices', '/finance/vouchers', '/finance/midtrans-payments'].some(r => location.pathname.startsWith(r));
  const isSupplierActive = ['/finance/purchase-orders', '/finance/suppliers'].some(r => location.pathname.startsWith(r));
  const isPusatInfoActive = ['/finance/knowledge-center', '/finance/tutorial', '/finance/announcements', '/finance/apa-yang-baru'].some(r => location.pathname.startsWith(r));
  const isChatActive = location.pathname.startsWith('/finance/chats') || location.pathname === '/finance/coordination-channels';
  
  // Feb 2026 — Parent Chat badge dari unified endpoint (cocok dengan role
  // lain). Sum per-room lama tetap tersedia untuk sub-menu bila diperlukan.
  const totalChatUnread = chatBadgeTotal || (unreadChats.admin + unreadChats.gudang + unreadChats.marketing);

  return (
    <div className="min-h-screen bg-gray-50 lg:overflow-hidden lg:h-screen" style={{ minHeight: '100dvh' }}>
      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - FIXED on desktop (>=1024px) */}
      <div className={`
        fixed inset-y-0 left-0 z-40
        w-64 bg-white border-r border-gray-200 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} style={{ height: '100dvh', maxHeight: '100dvh' }}>
        {/* Menu Items (sidebar header removed — branding now in MobileStickyHeader) */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto pt-14 lg:pt-4">
          {/* Dashboard */}
          <StandaloneMenuItem
            to="/finance/dashboard"
            icon={LayoutDashboard}
            label="Dashboard"
            onClick={closeSidebar}
            isActive={location.pathname === '/finance/dashboard'}
          />

          {/* Papan Operasional */}
          <StandaloneMenuItem
            to="/finance/papan-operasional"
            icon={Megaphone}
            label="Papan Operasional"
            onClick={closeSidebar}
            isActive={location.pathname.startsWith('/finance/papan-operasional')}
          />

          {/* Feb 2026 — Manajemen Order (READ-ONLY untuk finance).
              Buka daftar order + detail order lengkap; semua tombol aksi
              disembunyikan pada tampilan finance (guard di halamannya
              via useLocation). */}
          <MenuGroup
            icon={ClipboardList}
            label="Manajemen Order"
            title="Manajemen Order (Read-only)"
            isExpanded={expandedGroups.orders}
            onToggle={() => toggleGroup('orders')}
            isActive={location.pathname.startsWith('/finance/orders')}
          >
            <MenuItem
              to="/finance/orders"
              icon={ClipboardList}
              label="Daftar Order"
              onClick={closeSidebar}
              isActive={location.pathname === '/finance/orders' || location.pathname.startsWith('/finance/orders/')}
            />
          </MenuGroup>

          {/* Manajemen Invoice - Collapsible */}
          {canSeeInvoiceMenu && (
          <MenuGroup
            icon={Receipt}
            label="Manajemen Invoice"
            isExpanded={expandedGroups.invoice}
            onToggle={() => toggleGroup('invoice')}
            isActive={isInvoiceActive}
          >
            <MenuItem
              to="/finance/invoices"
              icon={Receipt}
              label="Daftar Invoice"
              onClick={closeSidebar}
              isActive={location.pathname === '/finance/invoices' || location.pathname.startsWith('/finance/invoices/')}
            />
            <MenuItem
              to="/finance/invoices/no-proof"
              icon={FileText}
              label="Tanpa Bukti"
              title="Invoice Tanpa Bukti Transfer"
              onClick={closeSidebar}
              isActive={location.pathname === '/finance/invoices/no-proof'}
            />
            <MenuItem
              to="/finance/midtrans-payments"
              icon={CreditCard}
              label="Pembayaran Midtrans"
              onClick={closeSidebar}
              isActive={location.pathname === '/finance/midtrans-payments'}
            />
          </MenuGroup>
          )}

          {/* Pembayaran Supplier - Collapsible */}
          {canSeeSupplierMenu && (
          <MenuGroup
            icon={Truck}
            label="Bayar Supplier"
            title="Pembayaran Supplier"
            isExpanded={expandedGroups.supplier}
            onToggle={() => toggleGroup('supplier')}
            isActive={isSupplierActive}
          >
            <MenuItem
              to="/finance/purchase-orders"
              icon={ShoppingCart}
              label="Surat Pesanan"
              title="Daftar Surat Pesanan"
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/finance/purchase-orders')}
            />
          </MenuGroup>
          )}

          {/* Pusat Informasi - Collapsible */}
          <MenuGroup
            icon={BookOpen}
            label="Pusat Info"
            title="Pusat Informasi"
            badge={unreadAnnouncements + appUpdatesCount}
            isExpanded={expandedGroups.pusatInfo}
            onToggle={() => toggleGroup('pusatInfo')}
            isActive={isPusatInfoActive}
          >
            <MenuItem
              to="/finance/announcements"
              icon={Bell}
              label="Pengumuman"
              badge={unreadAnnouncements}
              onClick={closeSidebar}
              isActive={location.pathname.startsWith('/finance/announcements')}
            />
            <MenuItem
              to="/finance/tutorial"
              icon={BookOpen}
              label="Tutorial"
              onClick={closeSidebar}
              isActive={location.pathname === '/finance/tutorial'}
            />
            <MenuItem
              to="/finance/apa-yang-baru"
              icon={Sparkles}
              label="Apa yang Baru"
              badge={appUpdatesCount}
              onClick={closeSidebar}
              isActive={location.pathname === '/finance/apa-yang-baru'}
            />
          </MenuGroup>

          {/* Chat — Fix 90 (Feb 2026): submenu dihapus, langsung ke Marketing chat.
              Finance tidak punya /finance/all-chats — landing di /finance/chats/marketing
              (destinasi utama) dan sisanya bisa diakses via halaman itu sendiri.
          */}
          <StandaloneMenuItem
            to="/finance/chats/marketing"
            icon={MessageSquare}
            label="Chat"
            badge={totalChatUnread}
            onClick={closeSidebar}
            isActive={isChatActive}
          />

          {/* Okt 2026 — Akun Finance yang juga dicentang Marketing: pintu masuk ke menu Marketing */}
          {(user?.assigned_roles || []).map((r) => String(r).toLowerCase()).includes("marketing") && (
            <StandaloneMenuItem
              to="/marketing/dashboard"
              icon={Megaphone}
              label="Menu Marketing"
              onClick={closeSidebar}
              isActive={false}
              highlight
            />
          )}
        </nav>

        {/* Bottom: Profile row (with bell right) + Logout row */}
        <div className="flex-shrink-0 p-2 border-t border-gray-200 bg-white" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
          <div className="flex items-center gap-2 mb-2">
            <Link
              to="/finance/profile"
              onClick={closeSidebar}
              className="flex items-center gap-2 flex-1 min-w-0 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="profile-link"
            >
              <User className="w-5 h-5 text-gray-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate leading-tight">{user?.name}</p>
                <p className="text-[10px] text-gray-500 truncate leading-tight">{user?.user_id} · Finance</p>
              </div>
            </Link>

            <Link
              to="/finance/notifications"
              onClick={closeSidebar}
              aria-label="Notifikasi"
              className="relative flex items-center justify-center w-11 h-11 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0"
              data-testid="sidebar-notification-bell"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {unreadNotifications > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold min-w-[16px] h-4 flex items-center justify-center px-1 rounded-full">
                  {unreadNotifications > 99 ? '99+' : unreadNotifications}
                </span>
              )}
            </Link>
          </div>

          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            size="sm"
            className="w-full justify-center text-red-600 border-red-200 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content - Scrollable area, offset by sidebar width on desktop */}
      <div className="flex-1 min-w-0 min-h-screen lg:ml-64 lg:h-screen lg:overflow-y-auto">
        {/* Mobile Sticky Header — Feb 2026 (v2 redesign) */}
        <MobileStickyHeader
          userName={user?.name || 'User'}
          userRole="Finance"
          sidebarOpen={sidebarOpen}
          onMenuClick={() => setSidebarOpen(!sidebarOpen)}
        />
        <div className="w-full h-full overflow-y-auto pt-14 lg:pt-0 pb-24">
          <PullToRefresh>
            <div className="w-full max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6">
              <Outlet />
            </div>
          </PullToRefresh>
        </div>
      </div>
    </div>
  );
}
